import * as React from 'react';

import {Button} from 'shells/button/button';
import {EmbedOrShareModal} from 'shells/embed-or-share-modal/embed-or-share-modal';
import {ChevronDownIcon} from 'shells/icon';
import {Popover} from 'shells/popover/popover';
import {MeetingCustomizerWrapper} from 'shells/scheduler/meeting-customizer-wrapper';

import type {GetSessionUser_loggedInUser as User} from 'nutshell-graphql-types';

import {useGetSubdomains} from '../../core/subdomains/graphql/hooks/use-get-subdomains';
import {useGetInstanceInfo} from '../../core/use-get-instance-info';
import {createSubdomainOption} from '../../setup/domains/helpers';

import {useGetSchedulerPageConfig, useUpdateSchedulerPageConfig} from '../graphql/hooks';
import {SchedulerFullFeatureGateModal} from '../scheduler-full-feature-gate-modal';

import styles from './meeting-page-button.module.css';

type Props = {
    user: User;
    refetchMeetings: () => Promise<any>;
};

export function MeetingPageButton(props: Props) {
    const [isPopoverOpen, setIsPopoverOpen] = React.useState<boolean>(false);
    const [isFeatureGateModalOpen, setIsFeatureGateModalOpen] = React.useState<boolean>(false);
    const [isShareModalOpen, setIsShareModalOpen] = React.useState<boolean>(false);

    const {sessionInstance} = useGetInstanceInfo();
    const {subdomains} = useGetSubdomains(true);
    const {schedulerPageConfig, isLoading: isLoadingSchedulerPageConfig} =
        useGetSchedulerPageConfig(props.user.id);
    const {updateSchedulerPageConfig} = useUpdateSchedulerPageConfig();

    const buttonRef = React.useRef(null);

    const subdomainOptions = React.useMemo(() => {
        return subdomains
            .filter((subdomain) => subdomain.isVerified)
            .map((subdomain) => createSubdomainOption(subdomain));
    }, [subdomains]);

    return (
        <>
            <Button
                variant='secondary'
                size='big'
                getButtonRef={buttonRef}
                onClick={() => {
                    setIsPopoverOpen(true);
                }}
            >
                <div className={styles['meeting-page-button-content']}>
                    <div>Your meeting page</div>
                    <ChevronDownIcon />
                </div>
            </Button>
            {isPopoverOpen && (
                <Popover
                    anchor={buttonRef.current}
                    location='bottom'
                    onBlur={() => {
                        setIsPopoverOpen(false);
                    }}
                >
                    <div className={styles['popover-content-container']}>
                        <div className={styles['popover-content-text']}>
                            All <strong>active and public meetings</strong> are displayed on your
                            meeting page. This makes it easy for invited participants to select
                            which meeting they would like to book.
                        </div>
                        <div>
                            <MeetingCustomizerWrapper
                                meetingPageUrl={props.user.schedulerRootMeetingsUrl}
                                pageConfig={schedulerPageConfig}
                                allowedToCustomizeUrl={!NutClientConfig.isTrial}
                                hasSchedulerFullAccess={
                                    sessionInstance
                                        ? sessionInstance.schedulerAccessType === 'FULL_ACCESS'
                                        : false
                                }
                                hasUploadedAvatar={props.user.hasUploadedAvatar}
                                subdomainOptions={subdomainOptions}
                                refetchMeetings={props.refetchMeetings}
                                onUpdatePageConfig={(updatePageConfigInput) => {
                                    return updateSchedulerPageConfig(updatePageConfigInput);
                                }}
                                onOpenFeatureGateModal={() => {
                                    setIsPopoverOpen(false);
                                    setIsFeatureGateModalOpen(true);
                                }}
                            />
                        </div>
                        <Button
                            variant='secondary'
                            size='big'
                            disabled={isLoadingSchedulerPageConfig || !schedulerPageConfig}
                            onClick={() => {
                                setIsPopoverOpen(false);
                                setIsShareModalOpen(true);
                            }}
                        >
                            Embed on your website
                        </Button>
                    </div>
                </Popover>
            )}
            {isShareModalOpen && schedulerPageConfig && (
                <EmbedOrShareModal
                    onClose={() => setIsShareModalOpen(false)}
                    header='Share your meeting page'
                    codeSnippet={schedulerPageConfig.embedSnippet}
                    embedInfoPopoverText="Copy and paste this code snippet into your website where you'd like to show your meeting page."
                    copyableLinkLabel='Meeting page link'
                    copyableLink={props.user.schedulerRootMeetingsUrl}
                />
            )}
            <SchedulerFullFeatureGateModal
                isOpen={isFeatureGateModalOpen}
                onClose={() => setIsFeatureGateModalOpen(false)}
                isAdmin={props.user.permissions.canAccessSetup}
            />
        </>
    );
}
